import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createAppTheme } from "./theme.config.js";
export { DEFAULT_PRIMARY, DEFAULT_SECONDARY } from "./theme.config.js";
const ThemeContext = createContext(undefined);
export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used within ThemeContextProvider");
    }
    return context;
};
export const ThemeContextProvider = ({ children, brand }) => {
    // Initialize from localStorage or default to light
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem("theme-mode");
        return savedMode || "light";
    });
    // Detect system theme preference
    const [systemPreference, setSystemPreference] = useState(() => {
        if (typeof window !== "undefined" && window.matchMedia) {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return "light";
    });
    // Listen for system theme changes
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia)
            return;
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            setSystemPreference(e.matches ? "dark" : "light");
        };
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);
    // Persist to localStorage when mode changes
    useEffect(() => {
        localStorage.setItem("theme-mode", mode);
    }, [mode]);
    // Stable identities: consumers use them in effect dependencies (a new
    // function every render made such an effect undo the user's choice).
    const setThemeMode = useCallback((newMode) => {
        setMode(newMode);
    }, []);
    const toggleTheme = useCallback(() => {
        setMode((prevMode) => {
            if (prevMode === "light")
                return "dark";
            if (prevMode === "dark")
                return "system";
            return "light";
        });
    }, []);
    // Determine actual theme to use
    const actualMode = mode === "system" ? systemPreference : mode;
    // Create theme based on mode using the centralized createAppTheme function
    // This preserves all component overrides, typography, and other customizations
    const brandPrimary = brand?.primary;
    const brandSecondary = brand?.secondary;
    const theme = useMemo(() => createAppTheme(actualMode, { primary: brandPrimary, secondary: brandSecondary }), [actualMode, brandPrimary, brandSecondary]);
    return (_jsx(ThemeContext.Provider, { value: { actualMode, mode, setThemeMode, toggleTheme }, children: _jsxs(MuiThemeProvider, { theme: theme, children: [_jsx(CssBaseline, {}), children] }) }));
};
//# sourceMappingURL=ThemeContext.js.map